import './TasksContainer.scss'

import React from 'react'
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import styled from 'styled-components'
import { CSSTransition, TransitionGroup } from 'react-transition-group'


import { SubTitle } from '../../common/components'
import { TaskPayload } from './tasks-slice'

import TaskItem from './TaskItem'

const InnerTasksContainer = styled.div<{
  type: ContainerType
  items: number
}>`
  display: flex;
  flex-direction: column;

  & > *:not(:last-child) {
    margin-bottom: 5px;
  }

  ${({ type, items }) =>
    type === 'completed' && items > 0 ? 'margin-bottom: 28px' : ''};
`

const OuterContainer = styled.div<{ type: ContainerType; items: number }>`
  ${({ type, items }) =>
    type === 'open' && items > 0 ? 'margin-bottom: 18px' : ''};
`

const SubTitleContainer = styled.div`
  align-items: center;
  display: flex;

  & > *:first-child {
    margin-right: 14px;
  }
`

const Wrapper = styled.div`
  color: var(--sn-stylekit-foreground-color);
`

type ContainerType = 'open' | 'completed'

type TasksContainerProps = {
  groupName: string
  tasks: TaskPayload[]
  type: ContainerType
  testId?: string
}

const TasksContainer: React.FC<TasksContainerProps> = ({
  groupName,
  tasks,
  type,
  testId,
  children,
}) => {
  const taskIds = tasks.map(t => t.id)

  return (
    <OuterContainer data-testid={testId} type={type} items={tasks.length}>
      <Wrapper>
        <SubTitleContainer>
          <SubTitle>{type === 'completed' ? 'completed' : 'open tasks'}</SubTitle>
        </SubTitleContainer>
        <SortableContext
          items={taskIds}
          strategy={verticalListSortingStrategy}
        >
          <InnerTasksContainer
            className={`${type}-tasks-container`}
            items={tasks.length}
            type={type}
          >
            <TransitionGroup
              component={null}
              childFactory={(child) => React.cloneElement(child)}
            >
              {tasks.map((task) => {
                return (
                  <CSSTransition
                    key={`${task.id}-${!!task.completed}`}
                    classNames={{
                      enter: 'fade-in',
                      enterActive: 'fade-in',
                      enterDone: 'fade-in',
                      exit: 'fade-out',
                      exitActive: 'fade-out',
                      exitDone: 'fade-out',
                    }}
                    timeout={{
                      enter: 1_500,
                      exit: 1_250,
                    }}
                    onEnter={(node: HTMLElement) => {
                      node.classList.remove('explode')
                    }}
                    onEntered={(node: HTMLElement) => {
                      node.classList.remove('fade-in')

                      const completed = !!task.completed
                      completed && node.classList.add('explode')

                      node.addEventListener(
                        'animationend',
                        () => {
                          node.classList.remove('explode')
                        },
                        false
                      )
                    }}
                    onExited={(node: HTMLElement) => {
                      node.classList.remove('fade-out')
                    }}
                    addEndListener={(node, done) => {
                      done()
                    }}
                    mountOnEnter
                    unmountOnExit
                  >
                    <TaskItem
                      key={`task-item-${task.id}`}
                      task={task}
                      groupName={groupName}
                    />
                  </CSSTransition>
                )
              })}
            </TransitionGroup>
            {children}
          </InnerTasksContainer>
        </SortableContext>
      </Wrapper>
    </OuterContainer>
  )
}

export default TasksContainer
